import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Trophy, Star, Users, CheckCircle, Sparkles, LogOut, TrendingUp, Brain, Target, Briefcase, ClipboardCheck, Flame, Award, ChevronRight, Calendar, HelpCircle, FolderOpen, MapPin, User, Lightbulb, BookOpen, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Navigation from '@/components/Navigation';
import WeeklyHighlights from '@/components/WeeklyHighlights';
import StreakDisplay from '@/components/StreakDisplay';
import ProfileCompletionModal from '@/components/ProfileCompletionModal';
import AnnouncementModal from '@/components/AnnouncementModal';
import GoalReminders from '@/components/GoalReminders';
import GoalSpotlight from '@/components/GoalSpotlight';
import BadgeNotification from '@/components/BadgeNotification';
import NotificationBell from '@/components/NotificationBell';
import MandatoryCheckinModal from '@/components/MandatoryCheckinModal';
import ThursdayWinReminder from '@/components/ThursdayWinReminder';

const YAS_LOGO = '/yas-logo.png';

const YouthDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState(null);
  const [weeklyStatus, setWeeklyStatus] = useState({ checkin: false, trivia: false });
  const [assessments, setAssessments] = useState({ pre: false, post: false });
  const [newBadge, setNewBadge] = useState(null);
  const highlightsRef = useRef(null);

  const handleOpenAddWin = () => {
    // Scroll to highlights section and trigger add dialog
    const highlightsSection = document.querySelector('[data-testid="add-highlight-button"]');
    if (highlightsSection) {
      highlightsSection.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => highlightsSection.click(), 500);
    }
  };
  const [leadMonitor, setLeadMonitor] = useState(null);
  const [weeklyTrends, setWeeklyTrends] = useState(null);
  const [staleCourses, setStaleCourses] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    checkForNewBadges();
    fetchWeeklyTrends();
    fetchStaleCourses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkForNewBadges = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.new_badges?.length > 0) {
          setNewBadge(data.new_badges[0]);
        }
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  };

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [teamRes, checkinsRes, triviaRes, assessmentsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkins/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trivia/weekly`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/my`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (teamRes.ok) {
        const teamData = await teamRes.json();
        setTeam(teamData);
      }

      if (checkinsRes.ok) {
        const checkins = await checkinsRes.json();
        const thisWeek = Math.floor((Date.now() - new Date('2026-06-15').getTime()) / (1000 * 60 * 60 * 24 * 7)) + 1;
        const hasCheckin = checkins.some(c => c.week_number === thisWeek);
        setWeeklyStatus(prev => ({ ...prev, checkin: hasCheckin }));
      }

      if (assessmentsRes.ok) {
        const assessmentData = await assessmentsRes.json();
        const hasPre = assessmentData.some(a => a.assessment_type === 'pre');
        const hasPost = assessmentData.some(a => a.assessment_type === 'post');
        setAssessments({ pre: hasPre, post: hasPost });
      }
      
      // Fetch lead monitor info if assigned
      if (user?.lead_monitor_id) {
        try {
          const monitorRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.lead_monitor_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (monitorRes.ok) {
            setLeadMonitor(await monitorRes.json());
          }
        } catch (e) {
          console.error('Error fetching lead monitor:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchWeeklyTrends = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/my-weekly-trends`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setWeeklyTrends(await res.json());
    } catch (e) { console.error('Error fetching trends:', e); }
  };

  const fetchStaleCourses = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses/available`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const courses = await res.json();
        const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
        const stale = courses.filter(c => 
          c.current_module > 0 && !c.completed && c.updated_at && c.updated_at < threeDaysAgo
        );
        setStaleCourses(stale.slice(0, 2));
      }
    } catch (e) { console.error('Error fetching courses:', e); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Badge Notification */}
      <BadgeNotification badge={newBadge} onClose={() => setNewBadge(null)} />
      
      {/* Announcement Modal */}
      <AnnouncementModal user={user} />
      
      {/* Mandatory Weekly Check-in Modal */}
      <MandatoryCheckinModal user={user} onComplete={() => fetchDashboardData()} />
      
      {/* Thursday Win Reminder */}
      <ThursdayWinReminder user={user} onAddWin={handleOpenAddWin} />
      
      {/* Profile Completion Modal */}
      <ProfileCompletionModal user={user} onComplete={(updatedUser) => setUser(updatedUser)} />
      
      {/* Hero Header with Glass Effect */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden"
      >
        {/* Background Gradient */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #1E6B7B 100%)' }} />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/20 rounded-full blur-2xl" />
        
        <div className="relative px-4 md:px-6 pt-6 pb-8">
          <div className="max-w-4xl mx-auto">
            {/* Top Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <img 
                  src={YAS_LOGO} 
                  alt="Newark SYEP" 
                  className="w-12 h-12 object-contain bg-white rounded-xl p-1 shadow-lg"
                  data-testid="dashboard-logo"
                />
                <div className="text-white">
                  <p className="text-xs uppercase tracking-widest font-medium" style={{ color: "rgba(255,255,255,0.8)" }}>Newark SYEP</p>
                  <p className="text-sm font-semibold">Summer 2026</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <NotificationBell />
                <Button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Welcome & Points Section */}
            <div className="flex items-center gap-4 text-white mb-4">
              <Avatar className="w-16 h-16 ring-4 ring-white/30 shadow-lg">
                {user.profile_picture ? (
                  <AvatarImage src={user.profile_picture} alt={user.name} />
                ) : null}
                <AvatarFallback className="bg-white/20 text-white font-heading text-2xl font-bold">
                  {user.avatar_emoji || user.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <motion.h1 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mb-1"
                >
                  Hey, {user.name.split(' ')[0]}!
                </motion.h1>
                <div className="flex items-center gap-2 flex-wrap">
                  {user.program_type && (
                    <Badge className={`${user.program_type === 'upnext' ? 'bg-green-500' : 'bg-purple-500'} text-white text-xs`}>
                      {user.program_type === 'upnext' ? 'UpNEXT SYEP' : 'NextGEN Leader'}
                    </Badge>
                  )}
                  <p className="text-white/70 text-sm">Let&apos;s make today count</p>
                </div>
              </div>
            </div>
            
            {/* Worksite & Lead Monitor Info */}
            {(user.work_site || leadMonitor) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white/10 backdrop-blur rounded-xl p-3 mb-4 space-y-2"
              >
                {user.work_site && (
                  <div className="flex items-center gap-2 text-white/90">
                    <MapPin className="w-4 h-4 text-secondary" />
                    <div className="flex-1">
                      <span className="text-xs text-white/60 uppercase">Worksite</span>
                      <p className="font-medium text-sm">{user.work_site}</p>
                      {user.work_site_address && (
                        <a 
                          href={`https://maps.google.com/?q=${encodeURIComponent(user.work_site_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-secondary hover:underline"
                        >
                          {user.work_site_address} ↗
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {leadMonitor && (
                  <div className="flex items-center gap-2 text-white/90">
                    <User className="w-4 h-4 text-accent" />
                    <div className="flex-1">
                      <span className="text-xs text-white/60 uppercase">Lead Monitor</span>
                      <p className="font-medium text-sm">{leadMonitor.name}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Stats Cards - Bento Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Points Card - Large */}
              <motion.div
                data-testid="points-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="col-span-1 bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                    <Star className="w-4 h-4 text-accent-foreground" fill="currentColor" />
                  </div>
                  <span className="text-white/80 text-xs font-medium uppercase tracking-wider">Points</span>
                </div>
                <p className="font-heading text-4xl font-extrabold text-white tracking-tight">
                  {user.total_points.toLocaleString()}
                </p>
              </motion.div>

              {/* Streak Card */}
              <motion.div
                data-testid="streak-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25 }}
                whileHover={{ scale: 1.02 }}
                className="col-span-1 bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-coral flex items-center justify-center">
                    <Flame className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white/80 text-xs font-medium uppercase tracking-wider">Streak</span>
                </div>
                <p className="font-heading text-4xl font-extrabold text-white tracking-tight">
                  {user.current_streak || 0}<span className="text-lg text-white/60 ml-1">{(user.current_streak || 0) === 1 ? 'day' : 'days'}</span>
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Curved Bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-b from-transparent to-slate-50" />
      </motion.div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        
        {/* Quick Actions - Horizontal Scroll on Mobile */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {[
            { icon: Target, label: 'Goals', path: '/goals', color: 'from-emerald-500 to-teal-600' },
            { icon: Brain, label: 'AI Guide', path: '/ai-guide', color: 'from-violet-500 to-purple-600' },
            { icon: Lightbulb, label: 'Interests', path: '/interests', color: 'from-purple-500 to-violet-600' },
            { icon: Award, label: 'Skills', path: '/skills', color: 'from-amber-500 to-orange-600' },
            { icon: Briefcase, label: 'Project', path: '/summer-project', color: 'from-orange-500 to-amber-600' },
            { icon: FolderOpen, label: 'Vault', path: '/vault', color: 'from-indigo-500 to-purple-600' },
            { icon: Trophy, label: 'Rankings', path: '/leaderboard', color: 'from-yellow-500 to-orange-500' },
            { icon: FolderOpen, label: 'Resources', path: '/resources', color: 'from-amber-500 to-orange-500' },
            { icon: HelpCircle, label: 'Help', path: '/helpdesk', color: 'from-blue-500 to-indigo-600' },
          ].map((item, idx) => (
            <motion.button
              key={item.label}
              data-testid={`quick-${item.label.toLowerCase()}-btn`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + idx * 0.05 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(item.path)}
              className={`flex-shrink-0 bg-gradient-to-br ${item.color} rounded-2xl p-4 min-w-[100px] text-white shadow-lg`}
            >
              <item.icon className="w-6 h-6 mx-auto mb-2" />
              <p className="text-xs font-semibold text-center">{item.label}</p>
            </motion.button>
          ))}
        </div>

        {/* Weekly Highlights / Work Journal */}
        <motion.div 
          data-testid="highlights-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <WeeklyHighlights user={user} />
        </motion.div>

        {/* AI Weekly Trends */}
        {weeklyTrends?.has_data && (
          <motion.div
            data-testid="ai-trends-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42 }}
          >
            <Card className="bg-white rounded-2xl p-5 shadow-card border border-purple-100/50 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-full blur-2xl -mr-6 -mt-6" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground text-sm">Your AI Insights</h3>
                  {weeklyTrends.mood_emoji && (
                    <span className="text-lg">{weeklyTrends.mood_emoji}</span>
                  )}
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed mb-3">{weeklyTrends.insight}</p>
                {weeklyTrends.encouragement && (
                  <p className="text-sm font-medium text-purple-700 bg-purple-50 rounded-xl px-3 py-2 mb-2">{weeklyTrends.encouragement}</p>
                )}
                {weeklyTrends.tip && (
                  <p className="text-xs text-muted-foreground">Tip: {weeklyTrends.tip}</p>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Course Progress Nudge */}
        {staleCourses.length > 0 && (
          <motion.div
            data-testid="course-nudge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.43 }}
          >
            <Card className="bg-white rounded-2xl p-4 shadow-card border border-amber-100/50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                  <BookOpen className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <h3 className="font-heading font-bold text-foreground text-sm">Continue Where You Left Off</h3>
              </div>
              <div className="space-y-2">
                {staleCourses.map(course => (
                  <div
                    key={course.id}
                    onClick={() => navigate(`/course/${course.id}`)}
                    className="flex items-center gap-3 p-3 rounded-xl bg-amber-50/50 hover:bg-amber-50 cursor-pointer transition-colors"
                    data-testid={`nudge-course-${course.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Play className="w-4 h-4 text-amber-700" fill="currentColor" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{course.title}</p>
                      <p className="text-xs text-muted-foreground">Module {course.current_module} of {course.total_modules}</p>
                    </div>
                    <Progress value={(course.current_module / course.total_modules) * 100} className="w-16 h-1.5" />
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Goal Spotlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <GoalSpotlight user={user} />
        </motion.div>

        {/* Goal Reminders */}
        <GoalReminders user={user} />

        {/* This Week's Tasks */}
        <motion.section
          data-testid="weekly-tasks-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-foreground flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-primary" />
              </div>
              This Week
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            {!assessments.pre && (
              <motion.div
                data-testid="assessment-prompt-card"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => navigate('/assessment', { state: { type: 'pre' } })}
                className="relative overflow-hidden bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-5 cursor-pointer shadow-lg group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="font-heading text-lg font-bold">Skills Assessment</h3>
                      <p className="text-white/80 text-sm">Track your growth • 50 pts</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">NEW</span>
                    <ChevronRight className="w-5 h-5 text-white/60 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Pre-assessment completed */}
            {assessments.pre && !assessments.post && (
              <Card className="p-4 bg-emerald-50 border-emerald-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-emerald-900">Skills Tracker Done!</h3>
                    <p className="text-emerald-700 text-sm">Final assessment at end of summer</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Weekly Check-in */}
            <motion.div
              data-testid="checkin-task-card"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => !weeklyStatus.checkin && navigate('/checkin')}
              className={`bg-white rounded-2xl p-4 shadow-card border border-border/50 cursor-pointer transition-all ${
                weeklyStatus.checkin ? 'opacity-70' : 'hover:shadow-floating hover:border-primary/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    weeklyStatus.checkin ? 'bg-emerald-100' : 'bg-primary/10'
                  }`}>
                    <CheckCircle className={`w-5 h-5 ${
                      weeklyStatus.checkin ? 'text-emerald-600' : 'text-primary'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground">
                      {weeklyStatus.checkin ? 'Check-in Complete!' : 'Weekly Check-in'}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {weeklyStatus.checkin ? 'See you next week' : 'Share your experience • 20 pts'}
                    </p>
                  </div>
                </div>
                {weeklyStatus.checkin ? (
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </motion.div>

            {/* Trivia Challenge */}
            <motion.div
              data-testid="trivia-task-card"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => navigate('/trivia')}
              className="bg-white rounded-2xl p-4 shadow-card border border-border/50 cursor-pointer hover:shadow-floating hover:border-secondary/20 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground">Weekly Trivia</h3>
                    <p className="text-muted-foreground text-sm">Test your knowledge • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </motion.div>

            {/* Weekly Survey */}
            <motion.div
              data-testid="survey-task-card"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => navigate('/weekly-survey')}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-4 cursor-pointer hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-heading font-bold">Quick Survey</h3>
                    <p className="text-white/80 text-sm">30 seconds • 10 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/60" />
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Team Section */}
        {team && (
          <motion.div
            data-testid="team-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            onClick={() => navigate('/team')}
            className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-700 rounded-2xl p-5 cursor-pointer hover:shadow-xl transition-all"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-heading text-lg font-bold">{team.name}</h3>
                    <p className="text-white/70 text-sm">{team.members?.length} members</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl font-bold text-white">{team.total_points?.toLocaleString()}</p>
                  <p className="text-white/70 text-xs">team pts</p>
                </div>
              </div>
              <Button 
                data-testid="view-team-button" 
                className="w-full bg-white/20 hover:bg-white/30 border-none rounded-xl text-white font-medium"
              >
                View Team
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      <Navigation role="youth" />
    </div>
  );
};

export default YouthDashboard;
